// G3/G4 (g-banking track): bank accounts, statement import, Open Banking feeds and
// reconciliation. Constants shared by the API and the web app. Every posting goes through the
// ledger's postJournal() (docs/guides/technical-guide/12-accounting-ledger.md).

/** Statement file formats the importer reads. `auto` detects OFX/QFX, CAMT.053 or CSV. */
export const STATEMENT_FORMATS = ['ofx', 'qfx', 'csv', 'camt053'] as const;
export type StatementFormat = (typeof STATEMENT_FORMATS)[number];

/** Where a statement came from: a file, or a sync of an Open Banking feed. */
export const STATEMENT_SOURCES = [...STATEMENT_FORMATS, 'feed'] as const;
export type StatementSource = (typeof STATEMENT_SOURCES)[number];

export const STATEMENT_SOURCE_LABELS: Record<StatementSource, string> = {
  ofx: 'OFX file',
  qfx: 'QFX file',
  csv: 'CSV file',
  camt053: 'CAMT.053 file',
  feed: 'Bank feed',
};

export const STATEMENT_LINE_STATUSES = ['unreconciled', 'reconciled', 'excluded'] as const;
export type StatementLineStatus = (typeof STATEMENT_LINE_STATUSES)[number];

/** Date formats a CSV statement can use. */
export const CSV_DATE_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY', 'DD-MM-YYYY', 'DD.MM.YYYY', 'D MMM YYYY', 'YYYYMMDD'] as const;
export type CsvDateFormat = (typeof CSV_DATE_FORMATS)[number];

export const CSV_DELIMITERS = [',', ';', '\t', '|'] as const;

/**
 * What a statement line can be reconciled against (Xero: Match, Create, Transfer):
 * - `journal_line`: a line already posted to the bank's ledger account (a customer payment,
 *   bill payment, payout, refund, expense repayment or manual journal)
 * - `invoice`: an outstanding sales invoice; records the payment
 * - `create`: a new transaction to an account with a VAT rate (bank fees, interest, spend money)
 * - `transfer`: money moved to or from another of the business's bank accounts
 * Other tracks register their own kinds (G2: `bill`) with registerReconcileSource().
 */
export const RECONCILE_KINDS = ['journal_line', 'invoice', 'create', 'transfer'] as const;
export type ReconcileKind = (typeof RECONCILE_KINDS)[number];

export const RECONCILE_KIND_LABELS: Record<string, string> = {
  journal_line: 'Payment',
  invoice: 'Invoice',
  create: 'New transaction',
  transfer: 'Transfer',
  bill: 'Bill',
};

/** Bank rules: which field of a statement line a condition tests, and how. */
export const BANK_RULE_FIELDS = ['description', 'payee', 'reference', 'amount'] as const;
export type BankRuleField = (typeof BANK_RULE_FIELDS)[number];
export const BANK_RULE_TEXT_OPERATORS = ['contains', 'equals', 'starts_with', 'ends_with', 'not_contains'] as const;
export const BANK_RULE_AMOUNT_OPERATORS = ['eq', 'gt', 'gte', 'lt', 'lte'] as const;
export type BankRuleOperator = (typeof BANK_RULE_TEXT_OPERATORS)[number] | (typeof BANK_RULE_AMOUNT_OPERATORS)[number];
export const BANK_RULE_OPERATOR_LABELS: Record<BankRuleOperator, string> = {
  contains: 'contains',
  equals: 'is',
  starts_with: 'starts with',
  ends_with: 'ends with',
  not_contains: 'does not contain',
  eq: 'equals',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
};
export const BANK_RULE_DIRECTIONS = ['any', 'spend', 'receive'] as const;
export type BankRuleDirection = (typeof BANK_RULE_DIRECTIONS)[number];

/** Open Banking providers behind the BankFeedProvider interface. */
export const BANK_FEED_PROVIDERS = ['truelayer'] as const;
export type BankFeedProviderName = (typeof BANK_FEED_PROVIDERS)[number];
export const BANK_FEED_ENVIRONMENTS = ['sandbox', 'live'] as const;
export const BANK_FEED_STATUSES = ['pending', 'active', 'expired', 'revoked', 'error'] as const;
export type BankFeedStatus = (typeof BANK_FEED_STATUSES)[number];

/** UK Open Banking: the customer re-authorises account access every 90 days. */
export const BANK_CONSENT_DAYS = 90;
/** Days before a consent expires when admins are warned. */
export const BANK_CONSENT_WARN_DAYS = 7;

/** The web page the provider redirects back to after the customer consents. */
export const BANK_FEED_CALLBACK_PATH = '/banking/feeds/callback';
